import { useState } from 'react'
import { Clock, KeyRound, Loader2, ShieldCheck } from 'lucide-react'

import Modal from '@/components/common/Modal'
import { publishIOFlowConnector } from '@/api/integration'

interface Props {
  project: string
  open: boolean
  onClose: () => void
  /** The seed application the agent wants published, when it passed one. */
  seedJson?: unknown
  /** Sends the sentence back to the agent — a key name, never the code. */
  onPublished: (agentReply: string) => void
  /** The user chose to wait for an approval instead; the agent's turn ends. */
  onRequestApproval: () => void
}

/**
 * Authorize publishing a GLOBAL IOFlow connector.
 *
 * A global connector is shared with every tenant on the IOFlow instance, so
 * IOFlow requires an instance admin. Two ways out of that, and the user picks:
 *
 *  - an authorization code (an `ioflow_…` API key an instance admin issued).
 *    It is posted to the backend, used once against IOFlow, and never stored,
 *    logged, or returned. The agent continues in the same turn.
 *  - an approval request, which pauses the agent until somebody decides and
 *    then resumes it by itself.
 *
 * As with the credential form, the agent is never the thing that carries the
 * secret.
 */
export default function IOFlowPublishForm({
  project,
  open,
  onClose,
  seedJson,
  onPublished,
  onRequestApproval,
}: Props) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    if (!code.trim() || !seedJson) return
    setBusy(true)
    setError('')
    try {
      const res = await publishIOFlowConnector(project, seedJson, code.trim())
      setCode('') // no reason to keep somebody else's credential in memory
      if (!res.ok) {
        setError(res.message || 'IOFlow did not publish the connector.')
        return
      }
      onPublished(
        res.agent_reply ||
          `The connector was published in IOFlow. Use [ioflow_published=${res.app_key}] and continue.`,
      )
    } catch (e: any) {
      setCode('')
      setError(e?.response?.data?.detail || String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Publish a shared connector">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
          This connector would be shared with <strong>every tenant</strong> on the
          IOFlow instance, so it has to be published by an IOFlow instance admin.
        </p>

        <div
          style={{
            padding: '0.75rem',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontWeight: 600, fontSize: '0.9375rem' }}>
            <ShieldCheck size={14} /> I have an authorization code
          </div>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            An IOFlow instance admin can issue one from their API keys page. It is
            used once here and never stored.
          </p>
          <input
            type="password"
            autoComplete="new-password"
            value={code}
            placeholder="ioflow_…"
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit()
            }}
            style={{
              padding: '0.4rem 0.5rem',
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text)',
            }}
          />
          <button
            className="btn btn-primary btn-sm"
            disabled={!code.trim() || busy || !seedJson}
            onClick={submit}
            style={{ alignSelf: 'flex-start' }}
          >
            {busy ? <Loader2 size={12} className="spin" /> : <KeyRound size={12} />}
            Publish now
          </button>
        </div>

        <div
          style={{
            padding: '0.75rem',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontWeight: 600, fontSize: '0.9375rem' }}>
            <Clock size={14} /> Ask an admin to approve it
          </div>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            The request is filed and the admins are emailed. The agent stops here
            and picks the work back up on its own once a decision is made — you
            do not need to keep this open.
          </p>
          <button
            className="btn btn-secondary btn-sm"
            onClick={onRequestApproval}
            disabled={busy}
            style={{ alignSelf: 'flex-start' }}
          >
            Request approval
          </button>
        </div>

        {error && (
          <div style={{ fontSize: '0.9375rem', color: 'var(--color-error, #f44336)' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  )
}
